import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Helpers from "@/Helpers";
import InvoiceLineItemModal from "@modals/InvoiceLineItemModal";
import PaymentEditModal from "@modals/PaymentEditModal";
import { triggerBrowserDownload } from "@/utils/authenticatedFileDownload";

function computeRunningBalances(transactions) {
    const ascending = [...transactions].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    let running = 0;
    const balanceByKey = new Map();

    ascending.forEach((transaction) => {
        running += transaction.amount;
        balanceByKey.set(`${transaction.entityType}-${transaction.entityId}`, running);
    });

    return transactions.map((transaction) => ({
        ...transaction,
        runningBalance: balanceByKey.get(`${transaction.entityType}-${transaction.entityId}`) ?? 0
    }));
}

function buildTransactionCsv(transactions) {
    const headers = ["Date", "Entity", "Details", "Amount", "Running Balance"];
    const rows = transactions.map((transaction) => [
        Helpers.formatDate(transaction.date),
        transaction.entityType,
        transaction.description || "",
        transaction.amount,
        transaction.runningBalance
    ]);
    return [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
}

export default function CompanyTransactionHistory(props) {
    const { tabData, updateSpinner, updateAccountingData } = props;
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedInvoiceLineItemId, setSelectedInvoiceLineItemId] = useState(null);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);

    useEffect(() => {
        fetchTransactionHistory();
    }, [tabData.company?.id]);

    async function fetchTransactionHistory() {
        if (!tabData.company?.id) return;

        setLoading(true);
        setError(null);
        updateSpinner && updateSpinner(true);

        try {
            const resp = await RightTimeApi.getAsync(`/Company/${tabData.company.id}/TransactionHistory`);
            if (resp && resp.ok && resp.jsonData) {
                const transactions = resp.jsonData.transactionHistory || [];
                const sortedTransactions = transactions.sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                setTransactionHistory(computeRunningBalances(sortedTransactions));
            } else {
                setError("Failed to load transaction history");
                console.error("Failed to load transaction history:", resp.status, resp.statusText);
            }
        } catch (fetchError) {
            console.error("Error loading transaction history:", fetchError);
            setError("Error loading transaction history: " + fetchError.message);
        } finally {
            setLoading(false);
            updateSpinner && updateSpinner(false);
        }
    }

    function handleTransactionClick(transaction) {
        switch (transaction.entityType) {
            case "InvoiceLineItem":
                setSelectedInvoiceLineItemId(transaction.entityId);
                break;
            case "PaymentAllocation":
                setSelectedPaymentId(transaction.parentEntityId);
                break;
        }
    }

    function handleInvoiceLineItemModalClose() {
        setSelectedInvoiceLineItemId(null);
        if (updateAccountingData) {
            updateAccountingData();
        }
    }

    function handlePaymentEditModalClose() {
        setSelectedPaymentId(null);
        fetchTransactionHistory();
        if (updateAccountingData) {
            updateAccountingData();
        }
    }

    function getEntityDisplayName(entityType, entityId) {
        if (entityId) {
            return `${entityType} #${entityId}`;
        }
        return "pending";
    }

    function getAmountDisplay(amount) {
        const formattedAmount = Helpers.formatCurrency(Math.abs(amount));
        if (amount < 0) {
            return `(${formattedAmount})`;
        }
        return formattedAmount;
    }

    function getRowClass(amount) {
        if (amount < 0) {
            return "transaction-debit";
        }
        return "transaction-credit";
    }

    function exportTransactionHistory() {
        if (transactionHistory.length === 0) {
            return;
        }

        const csv = buildTransactionCsv(transactionHistory);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        triggerBrowserDownload(blob, `transaction-history-${tabData.company?.id || "company"}.csv`);
    }

    if (loading) {
        return (
            <div className="center">
                <p>Loading transaction history...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="center">
                <p className="error">{error}</p>
                <button onClick={fetchTransactionHistory} className="btn btn-primary">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="horizontal transactionHistoryPanel">
            <div className="transactionHistoryToolbar">
                <button
                    type="button"
                    className="btn btn-outline"
                    onClick={exportTransactionHistory}
                    disabled={transactionHistory.length === 0}
                >
                    Export CSV
                </button>
            </div>
            <div className="tableWrapper factTable scrollable transactionHistoryTable">
                <table>
                    <caption>
                        <span className="title">Transaction History</span>
                        {transactionHistory.length > 0 && (
                            <span className="captionCount">({transactionHistory.length})</span>
                        )}
                    </caption>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Entity</th>
                            <th>Details</th>
                            <th>Amount</th>
                            <th>Running Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactionHistory.length > 0 ? (
                            transactionHistory.map((transaction) => (
                                <tr
                                    key={`${transaction.entityType}-${transaction.entityId}`}
                                    className={`${getRowClass(transaction.amount)} pointer`}
                                    onClick={() => handleTransactionClick(transaction)}
                                    title="Click to view details"
                                >
                                    <td>{Helpers.formatDate(transaction.date)}</td>
                                    <td>
                                        {getEntityDisplayName(transaction.entityType, transaction.entityId)}
                                    </td>
                                    <td className="left">
                                        {transaction.description || "N/A"}
                                    </td>
                                    <td className="right">
                                        {getAmountDisplay(transaction.amount)}
                                    </td>
                                    <td className="right">
                                        {Helpers.formatCurrency(transaction.runningBalance)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="center">
                                    No transaction history found for this company
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selectedInvoiceLineItemId !== null && (
                <InvoiceLineItemModal
                    invoiceLineItemId={selectedInvoiceLineItemId}
                    companyId={tabData.company?.id}
                    invoiceId={null}
                    adminPerms={tabData.adminPerms}
                    onClose={handleInvoiceLineItemModalClose}
                    updateSpinner={updateSpinner}
                    onSave={handleInvoiceLineItemModalClose}
                />
            )}

            {selectedPaymentId !== null && (
                <PaymentEditModal
                    paymentId={selectedPaymentId}
                    companyId={tabData.company?.id}
                    onClose={handlePaymentEditModalClose}
                    updateSpinner={updateSpinner}
                />
            )}
        </div>
    );
}
